import { useState } from 'react';
import { SegmentOption, StanBarChart, StanCard, StanSegmentedControl, StanTag, StanTooltip } from '@/components';
import { useReferralsAnalytics } from '../queries/useReferralsQueries';
import { TimePeriod } from '../types/analytics.types';
import { createIconElement, formatCurrency } from '@/lib';
import { useUser } from '@/queries/useUser';
import UserIconSvg from '@/assets/icons/ic-user-01.svg';
import CommunityIconSvg from '@/assets/icons/ic-community.svg';
import DollarIconSvg from '@/assets/icons/ic-currency-dollar.svg';
import InfoIconSvg from '@/assets/icons/ic-info-circle.svg';

const UserIcon = createIconElement(UserIconSvg, { className: 'size-5 text-brand-main' });
const CommunityIcon = createIconElement(CommunityIconSvg, { className: 'size-5 text-brand-main' });
const DollarIcon = createIconElement(DollarIconSvg, { className: 'size-5 text-brand-main' });
const InfoIcon = createIconElement(InfoIconSvg, { className: 'size-4 text-gray' });

const TimePeriodAllTime: SegmentOption = {
    value: 'all-time',
    label: 'All Time',
};

const TimePeriodThisMonth: SegmentOption = {
    value: 'this-month',
    label: 'This Month',
};

const TimePeriodOptions: SegmentOption[] = [TimePeriodThisMonth, TimePeriodAllTime];

export function ReferralsAnalytics() {
    const [timePeriod, setTimePeriod] = useState<TimePeriod>('all-time');
    const { data, isLoading, isError } = useReferralsAnalytics();
    const { user } = useUser();

    const currency = user?.data?.currency || 'USD';

    if (isLoading) {
        return (
            <StanCard>
                <div className='text-gray text-label-sm-regular py-8 text-center'>Loading analytics...</div>
            </StanCard>
        );
    }

    if (isError || !data) {
        return (
            <StanCard>
                <div className='text-negative-active text-label-sm-regular py-8 text-center'>
                    Failed to load analytics data
                </div>
            </StanCard>
        );
    }

    const isThisMonth = timePeriod === 'this-month';

    // Get the appropriate data based on time period
    const chartLabels = isThisMonth ? data.this_month_daily_labels : data.all_time_monthly_labels;
    const chartData = isThisMonth ? data.this_month_daily_data : data.all_time_monthly_data;

    // Prepare chart series
    const chartSeries = [
        {
            name: isThisMonth ? 'Daily Referrals' : 'Monthly Referrals',
            data: chartData,
        },
    ];

    // Get the appropriate metrics based on time period
    const referralTrials = isThisMonth ? data.thisMonthTotalReferralTrialsStarted : data.totalReferrals;
    const activeReferrals = data.activeReferrals;
    const earnings = isThisMonth ? data.thisMonthsEarnings : data.estimated_commission;
    const roundedEarnings = Math.round(earnings);

    return (
        <div className='flex w-full max-w-full flex-col gap-6'>
            <StanSegmentedControl
                className='min-w-fit md:max-w-1/3'
                fullWidth={false}
                name='time-period'
                value={timePeriod}
                size='sm'
                onChange={value => setTimePeriod(value as TimePeriod)}
                options={TimePeriodOptions}
                aria-label='Time period selector'
            />

            <StanCard className='w-full max-w-full !items-stretch'>
                <div className='grid w-full grid-cols-1 gap-4 md:grid-cols-3'>
                    <div className='border-neutral-subtle/70 flex flex-col gap-3 rounded-2xl border bg-white p-4'>
                        <div className='flex items-center gap-3'>
                            {UserIcon}
                            <span className='text-gray text-label-sm-bold'>
                                Referral Trials ({isThisMonth ? 'This Month' : 'All Time'})
                            </span>
                        </div>
                        <div className='text-dark text-display-display2'>{referralTrials}</div>
                    </div>

                    <div className='border-neutral-subtle/70 flex flex-col gap-3 rounded-2xl border bg-white p-4'>
                        <div className='flex items-center gap-3'>
                            {CommunityIcon}
                            <span className='text-gray text-label-sm-bold'>Active Referrals (Now)</span>
                        </div>

                        <div className='flex items-center gap-2'>
                            <div className='text-dark text-display-display2'>{activeReferrals}</div>
                            {data.activeReferralTrials > 0 && (
                                <StanTag
                                    label={`+ ${data.activeReferralTrials} now in trial`}
                                    className='bg-positive-subtle text-positive-fg-primary-strong'
                                />
                            )}
                        </div>
                    </div>

                    <div className='border-neutral-subtle/70 flex flex-col gap-3 rounded-2xl border bg-white p-4'>
                        <div className='flex items-center gap-2'>
                            {DollarIcon}
                            <span className='text-gray text-label-sm-regular'>
                                Passive Income ({isThisMonth ? 'This Month' : 'Forecasted'})
                            </span>

                            {!isThisMonth && (
                                <StanTooltip
                                    position='bottom'
                                    content={
                                        <div className='text-label-xs-regular flex flex-col gap-2 p-2 text-left'>
                                            <div className='text-label-xs-bold'>Calculation</div>
                                            <div>
                                                Estimated Income Per Referral:{' '}
                                                <span className='text-label-xs-bold'>
                                                    {formatCurrency(data.estimated_commission_per_referral, 'USD')}
                                                </span>
                                            </div>
                                            <div>
                                                Active Referrals:{' '}
                                                <span className='text-label-xs-bold'>
                                                    {data.activeReferralsNotTrialing}
                                                </span>
                                            </div>
                                            <div>
                                                Estimated Income From Active Referrals:{' '}
                                                <span className='text-label-xs-bold'>
                                                    {formatCurrency(data.estimated_commission_not_trialing, 'USD')}
                                                </span>
                                            </div>
                                            <div>
                                                Trialing Referrals:{' '}
                                                <span className='text-label-xs-bold'>{data.activeReferralTrials}</span>
                                            </div>
                                            <div>
                                                Assumed Conversion Rate from Trialing to Active:{' '}
                                                <span className='text-label-xs-bold'>
                                                    {data.assumed_conversion_rate}
                                                </span>
                                            </div>
                                            <div>
                                                Estimated Income From Trialing Referrals:{' '}
                                                <span className='text-label-xs-bold'>
                                                    {formatCurrency(data.estimated_commission_trialing, 'USD')}
                                                </span>
                                            </div>
                                        </div>
                                    }
                                >
                                    <button className='flex items-center' aria-label='Show calculation'>
                                        {InfoIcon}
                                    </button>
                                </StanTooltip>
                            )}
                        </div>
                        <div className='text-dark text-display-display2'>
                            {formatCurrency(roundedEarnings, currency, undefined, 0)}
                            {isThisMonth ? '' : '/month'}
                        </div>
                    </div>
                </div>

                <div className='mt-6'>
                    <StanBarChart
                        series={chartSeries}
                        categories={chartLabels}
                        height={300}
                        color='var(--color-brand-main)'
                        borderRadius={4}
                        chartPrefix=''
                        decimals={0}
                    />
                </div>
            </StanCard>
        </div>
    );
}
